/**
 * Print Helper Utility
 * Handles printing functionality with mobile browser support
 */

/**
 * Deteksi apakah device adalah mobile
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Deteksi apakah browser adalah iOS Safari
 */
export const isIOSSafari = () => {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua);
  const iOSSafari = iOS && webkit && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
  return iOSSafari;
};

/**
 * Print function yang mobile-friendly
 * @param {Object} options - Print options
 * @param {Function} options.onBeforePrint - Callback sebelum print
 * @param {Function} options.onAfterPrint - Callback setelah print
 * @param {string} options.documentTitle - Title untuk document yang diprint
 */
export const handleMobileFriendlyPrint = (options = {}) => {
  const { onBeforePrint, onAfterPrint, documentTitle } = options;

  // Set document title jika ada
  if (documentTitle) {
    const originalTitle = document.title;
    document.title = documentTitle;

    // Restore title setelah print
    const restoreTitle = () => {
      document.title = originalTitle;
      if (onAfterPrint) onAfterPrint();
    };

    // Event listeners untuk after print
    window.addEventListener('afterprint', restoreTitle, { once: true });

    // Fallback timeout untuk restore title jika event tidak fire
    setTimeout(restoreTitle, 2000);
  }

  // Callback before print
  if (onBeforePrint) {
    onBeforePrint();
  }

  // Add mobile-specific centering class before print
  if (isMobileDevice()) {
    const body = document.body;
    const html = document.documentElement;

    body.classList.add('mobile-printing');
    html.classList.add('mobile-printing');

    // Remove class after print
    const cleanup = () => {
      body.classList.remove('mobile-printing');
      html.classList.remove('mobile-printing');
    };

    window.addEventListener('afterprint', cleanup, { once: true });
    setTimeout(cleanup, 3000);
  }

  // Gunakan native window.print() yang lebih compatible dengan mobile
  try {
    // Small delay untuk memastikan DOM ready
    setTimeout(() => {
      window.print();
    }, 100);
  } catch (error) {
    console.error('Print error:', error);
    if (onAfterPrint) onAfterPrint();
  }
};

/**
 * Alternatif print dengan menampilkan instruksi untuk mobile
 * @param {Function} showToastFunction - Function untuk menampilkan toast/notification
 */
export const showMobilePrintInstructions = (showToastFunction) => {
  const isMobile = isMobileDevice();
  const isIOS = isIOSSafari();

  if (isMobile) {
    if (isIOS) {
      showToastFunction(
        'Untuk print di iOS: Tekan tombol Share > Print, atau gunakan Export ke Word',
        'info'
      );
    } else {
      showToastFunction(
        'Untuk hasil terbaik di mobile: Gunakan Export ke Word atau buka di Desktop',
        'info'
      );
    }
    return true;
  }
  return false;
};

/**
 * Setup print dengan deteksi mobile otomatis
 * @param {Object} printRef - React ref untuk element yang akan diprint
 * @param {Object} options - Print options
 */
export const setupSmartPrint = (printRef, options = {}) => {
  const { onSuccess, documentTitle, showToastFunction } = options;

  return () => {
    // Jika mobile, tampilkan instruksi
    if (isMobileDevice() && showToastFunction) {
      const instructionShown = showMobilePrintInstructions(showToastFunction);

      // Tetap coba print, tapi user sudah diberi peringatan
      if (instructionShown) {
        // Delay sedikit agar toast muncul dulu
        setTimeout(() => {
          handleMobileFriendlyPrint({
            onAfterPrint: onSuccess,
            documentTitle
          });
        }, 1500);
        return;
      }
    }

    // Desktop atau jika tidak ada toast function, langsung print
    handleMobileFriendlyPrint({
      onAfterPrint: onSuccess,
      documentTitle
    });
  };
};

/**
 * Add print styles programmatically
 * @param {string} printableId - ID element yang akan diprint
 */
export const addPrintStyles = (printableId) => {
  const styleId = `print-styles-${printableId}`;

  // Check jika style sudah ada
  if (document.getElementById(styleId)) {
    return;
  }

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #${printableId},
      #${printableId} * {
        visibility: visible !important;
      }
      #${printableId} {
        position: absolute !important;
        left: 0 !important;
        top: 0 !important;
        width: 100% !important;
      }
      
      /* Hide print buttons */
      button,
      .no-print {
        display: none !important;
      }
    }
  `;
  document.head.appendChild(style);
};

/**
 * Remove print styles
 * @param {string} printableId - ID element
 */
export const removePrintStyles = (printableId) => {
  const styleId = `print-styles-${printableId}`;
  const style = document.getElementById(styleId);
  if (style) {
    style.remove();
  }
};
